"use client";

import { PanelLeftOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";

import { useAuth } from "@/components/auth/auth-provider";
import { TopHeader } from "@/components/nav/top-header";
import { ChatPanel } from "@/components/recruiter/chat-panel";
import { ConversationList } from "@/components/recruiter/conversation-list";
import { ShortlistPanel } from "@/components/recruiter/shortlist-panel";
import { useRecruiterChat } from "@/hooks/use-recruiter-chat";

// ---------------------------------------------------------------------------
// useIsMobile hook (inline)
// ---------------------------------------------------------------------------

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    setIsMobile(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [breakpoint]);

  return isMobile;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function RecruiterContent() {
  const { user, isLoading: authLoading } = useAuth();
  const chat = useRecruiterChat();
  const initialized = useRef(false);
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const router = useRouter();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<"chat" | "shortlist">("chat");

  // On mount: load conversation from URL ?id= or start fresh
  useEffect(() => {
    if (!authLoading && user && !initialized.current) {
      initialized.current = true;
      const urlId = searchParams.get("id");
      if (urlId && !isNaN(Number(urlId))) {
        chat.loadConversation(Number(urlId));
      } else {
        chat.startNewConversation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  // Sync URL when conversation changes
  useEffect(() => {
    if (!initialized.current) return;
    const urlId = searchParams.get("id");
    const currentUrlId = urlId ? Number(urlId) : null;
    if (chat.conversationId && chat.conversationId !== currentUrlId) {
      router.replace(`/recruiter?id=${chat.conversationId}`);
    } else if (!chat.conversationId && urlId) {
      router.replace("/recruiter");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.conversationId]);

  // Auth gate
  if (authLoading) {
    return (
      <>
        <TopHeader />
        <div style={styles.loadingContainer}>
          <p style={{ color: "var(--text-secondary)" }}>Loading...</p>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <TopHeader />
        <div style={styles.loadingContainer}>
          <p style={{ color: "var(--text-secondary)" }}>
            Please{" "}
            <a
              href={`/login?next=${encodeURIComponent("/recruiter")}`}
              style={{ color: "var(--accent-blue)", textDecoration: "underline" }}
            >
              log in
            </a>{" "}
            to use the recruiter.
          </p>
        </div>
      </>
    );
  }

  // -- Shared panel elements -------------------------------------------------

  const chatPanel = (
    <ChatPanel
      messages={chat.messages}
      onSendMessage={chat.sendMessage}
      connectionState={chat.connectionState}
      searchProgress={chat.searchProgress}
      isLoading={chat.isLoading}
      error={chat.error}
    />
  );

  const shortlistPanel = (
    <ShortlistPanel
      shortlist={chat.shortlist}
      agentContext={chat.agentContext}
      shortlistId={chat.shortlistId}
      connectionState={chat.connectionState}
      onSendMessage={chat.sendMessage}
    />
  );

  // -- Mobile layout ---------------------------------------------------------

  if (isMobile) {
    return (
      <>
      <TopHeader />
      <div style={styles.mobileContainer}>
        {/* Tab bar */}
        <div style={styles.tabBar}>
          <button
            onClick={() => setMobileTab("chat")}
            style={tabStyle(mobileTab === "chat")}
          >
            Chat
          </button>
          <button
            onClick={() => setMobileTab("shortlist")}
            style={tabStyle(mobileTab === "shortlist")}
          >
            Shortlist
          </button>
        </div>

        {/* Tab content */}
        <div style={styles.mobileContent}>
          {mobileTab === "chat" ? chatPanel : shortlistPanel}
        </div>
      </div>
      </>
    );
  }

  // -- Desktop layout --------------------------------------------------------

  return (
    <>
    <TopHeader />
    <div style={styles.container}>
      {/* Left panel: Chat with sidebar overlay */}
      <div style={styles.chatPanel}>
        {/* Toolbar row: sidebar toggle + conversation title */}
        <div style={styles.toolbar}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={styles.sidebarToggle}
            aria-label="Toggle conversation sidebar"
          >
            <PanelLeftOpen size={18} />
          </button>
          <span style={styles.toolbarTitle}>
            {chat.conversationTitle || "New conversation"}
          </span>
        </div>

        {/* Conversation sidebar (absolute overlay) */}
        <div style={{ position: "relative", flex: 1, overflow: "hidden" }}>
          <ConversationList
            currentConversationId={chat.conversationId}
            onSelectConversation={async (id) => {
              await chat.loadConversation(id);
              setSidebarOpen(false);
            }}
            onNewConversation={async () => {
              await chat.startNewConversation();
              setSidebarOpen(false);
            }}
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
          />
          {chatPanel}
        </div>
      </div>

      {/* Right panel: Shortlist */}
      <div style={styles.shortlistPanel}>{shortlistPanel}</div>
    </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab style helper
// ---------------------------------------------------------------------------

function tabStyle(isActive: boolean): React.CSSProperties {
  return {
    flex: 1,
    padding: "0.75rem",
    textAlign: "center",
    background: "transparent",
    border: "none",
    borderBottom: isActive
      ? "2px solid var(--accent-blue)"
      : "2px solid transparent",
    color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
    fontWeight: isActive ? 600 : 400,
    fontSize: "0.9rem",
    cursor: "pointer",
    transition: "color 0.15s, border-color 0.15s",
  };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    height: "100vh",
    paddingTop: "72px",
    overflow: "hidden",
  },
  chatPanel: {
    width: "40%",
    minWidth: 320,
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid var(--border)",
  },
  shortlistPanel: {
    width: "60%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    paddingTop: "72px",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    padding: "0.5rem 1rem",
    borderBottom: "1px solid var(--border)",
    gap: "0.5rem",
    flexShrink: 0,
  },
  sidebarToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: "6px",
    border: "none",
    background: "transparent",
    color: "var(--text-secondary)",
    cursor: "pointer",
    flexShrink: 0,
    transition: "color 0.15s, background 0.15s",
  },
  toolbarTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "var(--text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    flex: 1,
  },
  // -- Mobile styles ---------------------------------------------------------
  mobileContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    paddingTop: "72px",
    overflow: "hidden",
  },
  tabBar: {
    display: "flex",
    borderBottom: "1px solid var(--border)",
    flexShrink: 0,
  },
  mobileContent: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
};
